import { isPlatformBrowser } from '@angular/common';
import { Injectable, ElementRef, Inject, PLATFORM_ID } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';

import { AKAMAI_IMPOLICY } from '../../tokens/cms.render-ui.tokens';

import { MediaResolved } from '@api/catalog/products/medias/medias.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { PlatformService } from '@core/services/platform/platform.service';
import { AkamaiOptions } from '@features/cms-render/domain/models/akamaiOptions';
import { LazyImage } from '@features/cms-render/domain/models/lazyImage';
import { AkamaiService } from '@shared/services/akamai.service';

@Injectable()

export class AssetsSharedService {
    constructor(
        @Inject(PLATFORM_ID) private readonly platformId: Record<string, unknown>,
        @Inject(WINDOW) private readonly window: CommonWindow,
        @Inject(AKAMAI_IMPOLICY) private readonly akamaiImpolicy: string,
        private readonly platformService: PlatformService,
    ) {}

    /**
     * Returns the absolut url of an image
     * @param asset asset url to be used as base
     * @param assetsUrl assets url
     */
    public getAbsoluteAssetUrl(asset: string, assetsUrl: string): string {
        let result: string;
        if (asset) {
            // eslint-disable-next-line no-param-reassign
            asset = asset.replace(/\\/g, '/'); // replace Windows backslash for forward slash
            const separator = assetsUrl.endsWith('/') ? '' : '/';
            result = asset.startsWith('http') ? asset : assetsUrl + separator + asset;
        } else {
            result = asset;
        }
        return result;
    }

    /**
     * Returns the URL for the current asset including Akamai Image Video Manager params if needed
     * @param asset asset url to be used as base
     * @param assetsUrl assets url
     * @param akamaiImageVideoManagerConfig Akamai params
     */
    public getAssetUrl(
        asset: string,
        assetsUrl: string,
        akamaiImageVideoManagerConfig?: AkamaiOptions,
    ): string | undefined {
        let url = this.getAbsoluteAssetUrl(asset, assetsUrl);
        if (!url) {
            return undefined;
        }

        // const _url = new URL(url);
        // if (akamaiImageVideoManagerConfig?.enabled && akamaiImageVideoManagerConfig.impolicy) {
        //     _url.searchParams.set('impolicy', akamaiImageVideoManagerConfig.impolicy);
        // }

        if (this.platformService.isBrowser()) {
            url = this.addAkamaiParamsIfNeeded(url, akamaiImageVideoManagerConfig);
        } else if (
            !this.platformService.isBrowser()
            && akamaiImageVideoManagerConfig.enabled
            && akamaiImageVideoManagerConfig.width > 0) {
            url = AkamaiService.useAkamaiImage(
                null,
                url,
                akamaiImageVideoManagerConfig.width,
                akamaiImageVideoManagerConfig.impolicy
            );
        }

        return url;
    }

    /**
     * Returns the with for the DOM element that will contain the image
     * @param elRef Reference to the DOM element
     */
    public getWidth(elRef?: ElementRef): number {
        const width: number = elRef?.nativeElement?.offsetWidth;
        return width;
    }

    /**
     * Returns the CommonStyleRenderComponent from
     * DOM element that will contain the image
     * @param akamaiWidth Reference of the AkamaiWidth
     * @param aspectRatioWidth Reference of the original with of the image
     * @param aspectRatioHeight Reference of the original height of the image
     */
    public getSkeletonProperties(
        akamaiWidth: number,
        aspectRatioWidth: number,
        aspectRatioHeight: number,
    ): {
            skeletonWidth: number;
            skeletonWidthUnit: string;
            skeletonHeight: number;
            skeletonHeightUnit: string;
        } {
        const skeletonWidth = akamaiWidth;
        const skeletonWidthUnit = 'px';
        const skeletonHeight = Math.ceil(akamaiWidth * aspectRatioHeight / aspectRatioWidth);
        const skeletonHeightUnit = 'px';

        return {
            skeletonWidth,
            skeletonWidthUnit,
            skeletonHeight,
            skeletonHeightUnit,
        };
    }

    /**
     * Returns product image data (as LazyDataInterface). Also includes imwidth akamai param
     */
    public productImageData(
        media: MediaResolved | undefined,
        width: number,
        assetsUrl: string,
        impolicy?: string,
    ): LazyImage | undefined {
        let productImageData;

        if (media) {
            const akamaiImageVideoManagerConfig: AkamaiOptions = { enabled: true, width };
            if (impolicy) akamaiImageVideoManagerConfig.impolicy = impolicy;
            const src = this.getAssetUrl(media.path, assetsUrl, akamaiImageVideoManagerConfig);
            productImageData = { src, width: media.size?.width, height: media.size?.height };
        }

        return productImageData;
    }

    private addAkamaiParamsIfNeeded(assetUrl: string, akamaiImageVideoManagerConfig?: AkamaiOptions): string {
        let _assetUrl = assetUrl;

        if (!assetUrl.includes('imwidth=') && akamaiImageVideoManagerConfig?.enabled) {
            const width = this.getStandardWidth(assetUrl, akamaiImageVideoManagerConfig.width);

            const impolicy = akamaiImageVideoManagerConfig?.impolicy ?? this.akamaiImpolicy;

            _assetUrl = AkamaiService.useAkamaiImage(this.window, assetUrl, width, impolicy);
        }

        return _assetUrl;
    }

    private getStandardWidth(assetUrl: string, width: number | null): number {
        const windowWidthFallback = this.window?.innerWidth;
        const defaultWidthFallback = 800;
        const standardMultiple: number = 100;

        if (!width || width === 0) {
            // fallbacks
            let fallbackType: 'defaultWidthFallback' | 'windowWidthFallback' = 'windowWidthFallback';
            // eslint-disable-next-line no-param-reassign
            width = windowWidthFallback;
            if (width <= 0) {
                fallbackType = 'defaultWidthFallback';
                // eslint-disable-next-line no-param-reassign
                width = defaultWidthFallback;
            }

            if (isPlatformBrowser(this.platformId)) {
                console.warn(`🔴 ${assetUrl} applied ${fallbackType} ${windowWidthFallback}`);
            }
        }

        return Math.ceil(width / standardMultiple) * standardMultiple;
    }

    // TODO ECOMDUTI-20989 Revisar todas los métodos que ya no se usan con el nuevo srcset y eliminarlos si es necesario
    public getOptimumImageWidth(
        imageHeight: number,
        imageWidth: number,
        windowHeight: number,
        windowWidth: number,
        density: number = this.window?.devicePixelRatio || 1,
    ): number {
        let result: number = 0;

        if (imageHeight <= windowHeight || imageWidth <= windowWidth) {
            return Math.ceil(imageWidth);
        }
        if (
            this.calculateSideRelation(imageHeight, windowHeight) <= this.calculateSideRelation(imageWidth, windowWidth)
        ) {
            result = Math.ceil(imageWidth * this.calculateSideRelation(imageWidth, windowWidth) * density);
            result = result <= imageWidth
                || Math.ceil(imageHeight * this.calculateSideRelation(imageWidth, windowWidth) * density) <= imageHeight
                ? result : imageWidth;
            result = result > windowWidth ? windowWidth : result;
            return result;
        }

        result = Math.ceil(imageWidth * this.calculateSideRelation(imageHeight, windowHeight) * density);
        result = result <= imageWidth
            || Math.ceil(imageHeight * this.calculateSideRelation(imageHeight, windowHeight) * density) <= imageHeight
            ? result : imageWidth;
        result = result > windowWidth ? windowWidth : result;
        return result;
    }

    private calculateSideRelation(imageSize: number, windowSize: number): number {
        return windowSize / imageSize;
    }
}
